import { useMemo, useState } from 'react';
import { Button, Callout, Card, Icon, Tag } from '@humanforce/mango-ui-react';
import type { PayRunApproverView } from './payRunApproverModel';
import { LifecycleStepper } from './LifecycleStepper';
import type { LifecycleStep } from './lifecycleStepperTypes';
import { PopoverGroupProvider } from './usePopoverGroup';
import { NotImplementedHint } from './PayRunNotImplemented';
import './PayRunApproverPanel.css';

interface PayRunApproverPanelProps {
  view: PayRunApproverView;
}

const commentDate = new Intl.DateTimeFormat('en-AU', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Australia/Sydney',
});

const formatCommentDate = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : commentDate.format(parsed);
};

/**
 * The PRR-402 approver lockup: a divided summary strip, one left-to-right approval chain and the
 * comments row. It replaces the payroll user's timeline and stat cards — an approver sees the money
 * they are attesting to, who else is in the chain and the conversation, not processing mechanics.
 */
export const PayRunApproverPanel = ({ view }: PayRunApproverPanelProps) => {
  const [showAllComments, setShowAllComments] = useState(false);
  const visibleComments = showAllComments ? view.comments : view.comments.slice(0, 1);
  const hiddenCount = view.comments.length - visibleComments.length;
  const steps = useMemo<LifecycleStep[]>(() => view.chain.map((step) => ({
    id: step.id,
    title: step.title,
    detail: step.detail,
    state: step.state === 'done' ? 'complete' : step.state,
    icon: step.state === 'current' ? 'person' : undefined,
    connectorPercent: step.connectorPercent ?? (step.state === 'done' ? 100 : 0),
    footer: { title: step.title, detail: step.detail },
  })), [view.chain]);

  return (
    <div className="mgo-stack mgo-gap-l">
      {view.decided
        ? (
          <Callout tone="positive" title="You approved this pay run" testId="pay-run-approver-banner">
            Your approval is recorded. The pay run now sits with the remaining approvers.
          </Callout>
        )
        : (
          <Callout tone="info" title="Pending your approval" testId="pay-run-approver-banner">
            Review the pay run details, then send through your approval.
          </Callout>
        )}

      <Card>
        <div className="mgo-stack mgo-gap-l" data-testid="pay-run-approver-panel">
          <div className="pay-run-approver-strip" data-testid="pay-run-approver-metrics">
            {view.metrics.map((metric) => (
              <div key={metric.id} className="pay-run-approver-strip__metric">
                <span className="mgo-text-xs mgo-color-muted">{metric.label}</span>
                <span className="mgo-cluster mgo-gap-xs mgo-align-center">
                  <span className={metric.hero ? 'pay-run-approver-strip__hero' : 'mgo-text-l mgo-weight-strong'}>
                    {metric.value}
                  </span>
                  {metric.unavailable && (
                    <NotImplementedHint
                      what={metric.label}
                      because="No API field supplies this figure, and it must not be derived in the browser."
                    />
                  )}
                </span>
              </div>
            ))}
            {view.status && (
              <div className="pay-run-approver-strip__status">
                <Tag label={view.status.label} tone={view.status.tone} testId="pay-run-approver-status" />
              </div>
            )}
          </div>

          <div className="pay-run-approver-chain">
            <PopoverGroupProvider>
              <LifecycleStepper
                steps={steps}
                currentStepId={view.chain.find((step) => step.state === 'current')?.id ?? null}
                motion="progress-line"
                label="Pay run approval progress"
                testId="pay-run-approver-chain"
                stepTestIdPrefix="pay-run-approver-step"
                menuTestIdPrefix="pay-run-approver-menu"
              />
            </PopoverGroupProvider>
          </div>

          <div className="pay-run-approver-comments" data-testid="pay-run-approver-comments">
            <span className="pay-run-approver-comments__icon" aria-hidden="true"><Icon name="document" /></span>
            {view.comments.length === 0
              ? (
                <span className="mgo-cluster mgo-gap-xs mgo-align-center mgo-text-s mgo-color-muted">
                  <span>No comments on this pay run.</span>
                  {/* The design shows a running thread; the API stores at most one comment per
                      approval decision, so there is nothing to thread yet. */}
                  <NotImplementedHint
                    what="A comment thread"
                    because="The API stores one comment per approval decision; comments made before a decision are not kept."
                    testId="pay-run-approver-comments-hint"
                  />
                </span>
              )
              : (
              <div className="mgo-stack mgo-gap-xs">
                {visibleComments.map((comment) => {
                  const at = formatCommentDate(comment.at);
                  return (
                    <p key={comment.id} className="mgo-text-s" style={{ margin: 0 }}>
                      <span className="mgo-weight-strong">{comment.author}{at ? `, ${at}` : ''}:</span>
                      {' '}
                      <span className="mgo-color-muted">“{comment.body}”</span>
                    </p>
                  );
                })}
              </div>
              )}
            {view.comments.length > 0 && (hiddenCount > 0 || showAllComments) && (
              <Button
                label={showAllComments ? 'View less' : `View ${hiddenCount} more comment${hiddenCount === 1 ? '' : 's'}`}
                variant="tertiary"
                size="small"
                testId="pay-run-approver-comments-toggle"
                onClick={() => setShowAllComments((open) => !open)}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
